import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";

const STATES = [
  {
    num: "01", id: "ground", name: "Ground", sanskrit: "Sthitaprajna",
    color: "#4CAF7A",
    tagline: "Physiological stability under pressure.",
    desc: "Ground is the foundation every other state rests on. It is the capacity to remain physiologically regulated when the environment is not — to think clearly when the room is in chaos, to act without reactivity when the stakes are highest.",
    neuroscience: "HRV coherence · Prefrontal activation · Cortisol regulation",
    practice: "Kapalbhati + Rechaka",
  },
  {
    num: "02", id: "observe", name: "Observe", sanskrit: "Sakshi",
    color: "#4A90D9",
    tagline: "Witness consciousness. Reading without reacting.",
    desc: "Observe is the capacity to read a situation accurately — the room, the people, the dynamics — without being pulled into it. It is the difference between reacting from inside the system and perceiving it from outside.",
    neuroscience: "Insula activation · Interoceptive accuracy · Default mode suppression",
    practice: "Nadi Shodhana",
  },
  {
    num: "03", id: "connect", name: "Connect", sanskrit: "Maitri",
    color: "#C07FD9",
    tagline: "Resonance and trust. Others bring their best.",
    desc: "Connect is the state that creates the conditions for others to bring their full capability. It is not warmth as a performance — it is genuine resonance, the neurological capacity to be present with another person without an agenda.",
    neuroscience: "Oxytocin · Mirror neurons · Vagal tone",
    practice: "Bhramari",
  },
  {
    num: "04", id: "execute", name: "Execute", sanskrit: "Kriya",
    color: "#D9844A",
    tagline: "Focused, decisive action. Drive without reactivity.",
    desc: "Execute is pure directed energy — the capacity to move decisively, sustain effort, and complete. Most leaders can access Execute when calm. The trained leader accesses it under pressure, without the reactivity that degrades decision quality.",
    neuroscience: "Dopaminergic drive · Basal ganglia activation · Norepinephrine",
    practice: "Trataka",
  },
  {
    num: "05", id: "integrate", name: "Integrate", sanskrit: "Viveka",
    color: "#D9C44A",
    tagline: "Pattern recognition across complexity.",
    desc: "Integrate is the state that makes sense of complexity. It is the capacity to hold multiple threads simultaneously, to see patterns others miss, and to synthesise them into strategic insight — not through analysis alone, but through a quality of mind that is simultaneously expansive and precise.",
    neuroscience: "Default mode network · Gamma coherence · Hippocampal consolidation",
    practice: "Yoga Nidra",
  },
];

export const Framework = () => {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)" }}>

      {/* Header */}
      <div style={{ padding: "20px 24px 0", display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid var(--border)", paddingBottom: 20 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--muted)", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "DM Mono, monospace", letterSpacing: "0.06em" }}
        >
          <ArrowLeft size={14} />
          Back
        </button>
        <div style={{ flex: 1 }} />
        <a
          href="/framework.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--amber)", fontSize: 11, fontFamily: "DM Mono, monospace", letterSpacing: "0.12em", textDecoration: "none", textTransform: "uppercase" }}
        >
          Full page <ExternalLink size={11} />
        </a>
      </div>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "32px 24px 64px" }}>

        {/* Title block */}
        <div style={{ marginBottom: 40 }}>
          <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--amber)", marginBottom: 12 }}>
            The Leadership Framework
          </p>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, lineHeight: 1.1, color: "var(--text)", marginBottom: 16 }}>
            Five trainable states.<br />
            <em style={{ color: "var(--amber)" }}>One substrate.</em>
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.75, color: "var(--muted)", fontWeight: 300, maxWidth: 540 }}>
            Leadership isn't a set of competencies. It's a substrate — five distinct states of consciousness that effective leaders access fluidly, and that most leaders are partially locked out of under pressure.
          </p>
          <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--amber)", opacity: 0.7, marginTop: 16 }}>
            Biology before psychology
          </p>
        </div>

        {/* Fluidity principle */}
        <div style={{ background: "var(--surface2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "20px 24px", marginBottom: 40 }}>
          <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--amber)", marginBottom: 10 }}>
            Fluidity is the measure
          </p>
          <p style={{ fontSize: 13, lineHeight: 1.75, color: "var(--muted)", fontWeight: 300 }}>
            Range — how many states you can access. Precision — whether you reach for the right one. Recovery speed — how fast you return to Ground after pressure knocks you off it. These three variables, not personality type or leadership style, are what the framework trains.
          </p>
        </div>

        {/* Five states */}
        <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--dim)", marginBottom: 20 }}>
          The five leadership states
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {STATES.map((state) => (
            <div
              key={state.id}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              {/* Colour bar */}
              <div style={{ height: 2, background: state.color }} />

              <div style={{ padding: "20px 22px" }}>
                {/* State header */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.15em", color: "var(--dim)", marginBottom: 4 }}>
                      State {state.num}
                    </p>
                    <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: state.color, lineHeight: 1, marginBottom: 3 }}>
                      {state.name}
                    </h2>
                    <p style={{ fontFamily: "DM Mono, monospace", fontSize: 10, fontStyle: "italic", color: "var(--dim)", letterSpacing: "0.06em" }}>
                      {state.sanskrit}
                    </p>
                  </div>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: state.color, opacity: 0.15, flexShrink: 0, marginTop: 4 }} />
                </div>

                {/* Tagline */}
                <p style={{ fontSize: 13, color: "var(--text)", fontWeight: 500, marginBottom: 10 }}>
                  {state.tagline}
                </p>

                {/* Description */}
                <p style={{ fontSize: 13, lineHeight: 1.75, color: "var(--muted)", fontWeight: 300, marginBottom: 16 }}>
                  {state.desc}
                </p>

                {/* Footer chips */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 10px" }}>
                    <p style={{ fontFamily: "DM Mono, monospace", fontSize: 8, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--dim)", marginBottom: 2 }}>Neuroscience</p>
                    <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: state.color }}>{state.neuroscience}</p>
                  </div>
                  <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 10px" }}>
                    <p style={{ fontFamily: "DM Mono, monospace", fontSize: 8, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--dim)", marginBottom: 2 }}>Trained by</p>
                    <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "var(--muted)" }}>{state.practice}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ marginTop: 40, background: "var(--surface2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "24px", textAlign: "center" }}>
          <p style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--dim)", marginBottom: 12 }}>
            Begin the practice
          </p>
          <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: "var(--text)", marginBottom: 16 }}>
            Train the substrate.<br /><em style={{ color: "var(--amber)" }}>The performance follows.</em>
          </p>
          <button
            onClick={() => navigate("/protocol")}
            style={{ background: "var(--amber)", color: "var(--bg)", border: "none", borderRadius: 8, padding: "12px 28px", fontFamily: "DM Mono, monospace", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer", fontWeight: 600 }}
          >
            Begin Your Protocol
          </button>
        </div>

      </div>
    </div>
  );
};
